/**
 * ServiceWorkerUpdateWatcher — відстежує появу нової версії Service Worker
 * і виставляє pwaManager.updateAvailable, щоб UpdatePrompt показав тост.
 *
 * НАВІЩО. registerSW.js (VitePWA, injectRegister: 'script') реєструє SW на
 * КОЖНІЙ сторінці, але повний ServiceWorkerRegistrar (з підпискою на Web
 * Push) монтується лише у водійському layout. У диспетчерському layout ніхто
 * не слухав оновлення, тож новий SW після деплою назавжди лишався у стані
 * "waiting", а старий і далі віддавав закешований бандл — користувачі не
 * бачили нових змін, поки не закривали всі вкладки.
 *
 * Цей компонент — лише спостерігач оновлень, без push-підписки (щоб не
 * просити дозвіл на сповіщення у диспетчерів).
 */

import { useEffect } from 'react'

import { pwaManager } from './PwaManager'

interface ServiceWorkerUpdateWatcherProps {
    /** Інтервал перевірки оновлень SW (мс). За замовчуванням — 15 хвилин */
    updateCheckInterval?: number
}

export function ServiceWorkerUpdateWatcher({ updateCheckInterval = 15 * 60 * 1000 }: ServiceWorkerUpdateWatcherProps) {
    useEffect(() => {
        if (!('serviceWorker' in navigator)) return

        let intervalId: ReturnType<typeof setInterval> | null = null
        let disposed = false

        const markWaiting = (worker: ServiceWorker | null) => {
            if (!worker || disposed) return
            pwaManager.setWaitingWorker(worker)
            pwaManager.setUpdateAvailable(true)
        }

        const handleUpdateFound = (registration: ServiceWorkerRegistration) => {
            const installing = registration.installing
            if (!installing) return

            installing.addEventListener('statechange', () => {
                // "installed" за наявності controller = нова версія чекає на активацію
                if (installing.state === 'installed' && navigator.serviceWorker.controller) {
                    markWaiting(registration.waiting ?? installing)
                }
            })
        }

        navigator.serviceWorker.getRegistration().then((registration) => {
            if (!registration || disposed) return

            // Новий SW міг стати waiting ще до монтування (попередній деплой)
            if (registration.waiting && navigator.serviceWorker.controller) {
                markWaiting(registration.waiting)
            }

            registration.addEventListener('updatefound', () => handleUpdateFound(registration))

            intervalId = setInterval(() => {
                if (navigator.onLine) {
                    registration.update().catch(() => {
                        // Офлайн або мережева помилка — тихо ігноруємо
                    })
                }
            }, updateCheckInterval)
        })

        return () => {
            disposed = true
            if (intervalId !== null) clearInterval(intervalId)
        }
    }, [updateCheckInterval])

    return null
}
